import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Box, CssBaseline, IconButton, AppBar, Toolbar, Typography, useMediaQuery, useTheme, CircularProgress } from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Sidebar from './components/Sidebar';
import SignIn from './pages/SignIn';
import ForgotPassword from './pages/ForgotPassword';
import Dashboard from './pages/Dashboard';
import Policies from './pages/Policies';
import FAQ from './pages/FAQ';
import Assets from './pages/Assets';
import LifeDeclaration from './pages/LifeDeclaration';
import DeathCertificate from './pages/DeathCertificate';
import Subscription from './pages/Subscription';
import PromoCodes from './pages/PromoCodes';
import Communications from './pages/Communications';
import Reports from './pages/Reports';
import InvoiceDownload from './pages/InvoiceDownload';
import Settings from './pages/Settings';
import ManageLifeDeclarationProcess from './pages/ManageLifeDeclarationProcess';
import LifeDeclarationReview from './pages/LifeDeclarationReview';
import LifeDeclarationReviewDetail from './pages/LifeDeclarationReviewDetail';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  console.log('🛡️ ProtectedRoute - loading:', loading, 'isAuthenticated:', isAuthenticated);

  if (loading) {
    console.log('⏳ Showing loading spinner...');
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #4E83BD 0%, #3A6A99 100%)',
        }}
      >
        <CircularProgress size={60} sx={{ color: 'white' }} />
      </Box>
    );
  }

  if (isAuthenticated) {
    console.log('✅ User is authenticated, showing protected content');
    return children;
  } else {
    console.log('❌ User not authenticated, redirecting to /signin');
    return <Navigate to="/signin" />;
  }
};

function App() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  return (
    <Router>
      <AuthProvider>
        <CssBaseline />
        <Routes>
          {/* Sign In Route */}
          <Route path="/signin" element={<SignIn />} />

          {/* Forgot Password Route */}
          <Route path="/forgot-password" element={<ForgotPassword />} />

        {/* Protected Routes */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <Box sx={{ display: 'flex', minHeight: '100vh' }}>
                {/* Mobile AppBar */}
                {isMobile && (
                  <AppBar
                    position="fixed"
                    sx={{
                      background: 'linear-gradient(135deg, #4E83BD 0%, #3A6A99 100%)',
                      zIndex: (theme) => theme.zIndex.drawer + 1,
                    }}
                  >
                    <Toolbar>
                      <IconButton
                        color="inherit"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{ mr: 2 }}
                      >
                        <MenuIcon />
                      </IconButton>
                      <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 700 }}>
                        Meri Zimmedari
                      </Typography>
                    </Toolbar>
                  </AppBar>
                )}

                {/* Sidebar */}
                <Sidebar mobileOpen={mobileOpen} handleDrawerToggle={handleDrawerToggle} />

                {/* Main Content */}
                <Box
                  component="main"
                  sx={{
                    flexGrow: 1,
                    width: { xs: '100%', md: `calc(100% - 240px)` },
                    mt: { xs: '64px', md: 0 },
                  }}
                >
                  <Routes>
                    <Route path="/" element={<Dashboard />} />
                    <Route path="/policies" element={<Policies />} />
                    <Route path="/faq" element={<FAQ />} />
                    <Route path="/assets" element={<Assets />} />
                    <Route path="/life-declaration" element={<LifeDeclaration />} />
                    <Route path="/death-certificate" element={<DeathCertificate />} />
                    <Route path="/subscription" element={<Subscription />} />
                    <Route path="/promo-codes" element={<PromoCodes />} />
                    <Route path="/communications" element={<Communications />} />
                    <Route path="/reports" element={<Reports />} />
                    <Route path="/invoice-download" element={<InvoiceDownload />} />
                    <Route path="/settings" element={<Settings />} />
                    <Route path="/life-declaration-process" element={<ManageLifeDeclarationProcess />} />
                    <Route path="/life-declaration-review" element={<LifeDeclarationReview />} />
                    <Route path="/life-declaration-review/:id" element={<LifeDeclarationReviewDetail />} />
                  </Routes>
                </Box>
              </Box>
            </ProtectedRoute>
          }
        />
        </Routes>
      </AuthProvider>
    </Router>
  );
}

export default App;
